import React from 'react';
import { useSearchParams } from 'react-router-dom';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Link from '@mui/material/Link';
import Alert from '@mui/material/Alert';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';

// Friendly, actionable explanation per failure reason emitted by the Gitea
// OAuth callback (/auth/gitea/callback -> /unauthorized?reason=...). Most
// reasons are transient sign-in hiccups, so the primary action is "try again".
const REASONS: Record<string, string> = {
  state:
    "Your sign-in session expired or your browser blocked the sign-in cookie. Please try again — and make sure cookies are enabled for femorph.com.",
  token:
    "We couldn't confirm your git.femorph.com account with the sign-in server. Please try again; if it keeps happening, contact an administrator.",
  userinfo:
    "We reached git.femorph.com but couldn't read your account details. Please try again.",
  username:
    "Your git.femorph.com account is missing a username, so we can't sign you in.",
  exception:
    "Something went wrong completing your sign-in. Please try again.",
};

const Unauthorized: React.FC = () => {
  const [params] = useSearchParams();
  const reason = params.get('reason') || '';
  const explanation =
    REASONS[reason] ||
    "We couldn't complete your sign-in. Please try again, or contact an administrator if you don't have a git.femorph.com account.";

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        p: 2,
      }}
    >
      <Paper elevation={3} sx={{ maxWidth: 460, width: '100%', p: 4, textAlign: 'center' }}>
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'action.hover',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            mb: 2,
          }}
        >
          <LockOutlinedIcon color="action" />
        </Box>

        <Typography variant="h5" gutterBottom>
          Sign-in didn&apos;t complete
        </Typography>

        <Alert severity="info" sx={{ textAlign: 'left', mb: 3 }}>
          {explanation}
        </Alert>

        <Button variant="contained" color="primary" fullWidth href="/auth/gitea" sx={{ mb: 1.5 }}>
          Try signing in again
        </Button>
        <Button variant="text" fullWidth href="/login">
          Back to login
        </Button>

        <Typography variant="body2" color="text.secondary" sx={{ mt: 3 }}>
          Need an account or help? Contact{' '}
          <Link href="mailto:akascap@gmail.com">an administrator</Link>.
        </Typography>

        {reason && (
          <Typography variant="caption" color="text.disabled" sx={{ display: 'block', mt: 2 }}>
            Reference: {reason}
          </Typography>
        )}
      </Paper>
    </Box>
  );
};

export default Unauthorized;
